import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { NewRecipe } from '@/components/admin/new-recipe';
import { Badge } from '@/components/ui';
import { adminRecipesQuery } from '@/queries';
import type { RecipeLine } from '@/lib/stock-types';

/**
 * Recipes. Spec 003 US-3, task T075, invariant R-1, FR-203.
 *
 * A recipe says what one unit of a product consumes when it is decorated by a
 * given method. The screen leads with the thing people get wrong about it: a
 * recipe a job has consumed against **cannot be edited**, only superseded.
 *
 * That is not fussiness. Change the numbers under a finished job and its cost
 * becomes fiction, and the job cost screen is what tells Guy whether a line of
 * work is worth doing. So a consumed recipe shows its lines read only and
 * offers a new version instead.
 *
 * There are no costs here. A recipe is a bill of materials; what those
 * materials cost belongs to the costs module and its permission (R-90).
 */
/**
 * Make the next version. T075 second half, invariant R-1, FR-209.
 *
 * The lines start as a copy of the current ones, because a new version is
 * almost always a change to one number rather than a fresh start, and asking
 * somebody to retype a bill of materials is how a digit gets lost.
 *
 * It is ONE request. Superseding means deactivating the old version, creating
 * the new one and adding its lines, in that order, because only one version may
 * be active. Three requests from a screen can leave a product with no active
 * recipe at all, which stops every job for it (R-104).
 */
function NewVersion({
  recipeId,
  lines,
  version,
}: {
  recipeId: string;
  lines: RecipeLine[];
  version: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() =>
    lines.map((l) => ({
      item_id: l.item_id,
      label: `${l.items?.code ?? ''} ${l.items?.name ?? ''}`.trim(),
      qty_per_unit: String(l.qty_per_unit),
      uom: l.uom,
    })),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/recipes/supersede', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          recipe_id: recipeId,
          lines: draft.map((d) => ({
            item_id: d.item_id,
            qty_per_unit: Number(d.qty_per_unit),
            uom: d.uom,
          })),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'recipes'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="m-3 rounded-[10px] border border-line px-3 py-2 text-[14px]"
      >
        Make version {version + 1}
      </button>
    );
  }

  const valid = draft.length > 0 && draft.every((d) => Number(d.qty_per_unit) > 0);

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}
      className="border-t border-line p-4"
    >
      <p className="mb-1 text-[15px] font-semibold">Version {version + 1}</p>
      <p className="mb-3 max-w-[64ch] text-[13px] text-muted">
        These start as a copy of version {version}. Change what has changed. The
        old version stays attached to the jobs that used it, so their costs keep
        meaning what they meant on the day.
      </p>

      <div className="grid gap-2">
        {draft.map((d, i) => (
          <label key={d.item_id} className="flex items-center gap-3 text-[14px]">
            <span className="w-64 truncate">{d.label}</span>
            <input
              type="number"
              step="any"
              min="0"
              value={d.qty_per_unit}
              onChange={(e) => {
                const next = [...draft];
                next[i] = { ...next[i]!, qty_per_unit: e.target.value };
                setDraft(next);
              }}
              className="h-9 w-28 rounded-[8px] border border-line bg-panel px-2 text-right"
            />
            <span className="text-[13px] text-muted">{d.uom} per unit</span>
            <button
              type="button"
              onClick={() => setDraft(draft.filter((_, j) => j !== i))}
              className="text-[13px] text-muted"
            >
              remove
            </button>
          </label>
        ))}
      </div>

      {draft.length === 0 && (
        <p className="mt-2 text-[13px] text-muted">
          A recipe with no lines cannot be consumed: a job cost of zero is never
          true, so this will be refused.
        </p>
      )}

      {error && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="submit"
          disabled={!valid || save.isPending}
          className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : `Publish version ${version + 1}`}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-[14px] text-muted">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminRecipesPage() {
  const { recipes, lines, products, methods, materials } =
    useSuspenseQuery(adminRecipesQuery).data;

  const byRecipe = new Map<string, typeof lines>();
  for (const l of lines) byRecipe.set(l.recipe_id, [...(byRecipe.get(l.recipe_id) ?? []), l]);

  // Newest version of each product and method first, superseded ones under it.
  const groups = new Map<string, typeof recipes>();
  for (const r of recipes) {
    const key = `${r.product_id}::${r.method_key}`;
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Recipes</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          What one unit consumes, per product and decoration method.
        </p>
      </div>

      <NewRecipe products={products} methods={methods} items={materials} />

      <div className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px] text-muted">
        A recipe that a job has already used <strong>cannot be changed</strong>. Make
        a new version instead. The old one stays attached to the jobs that used
        it, so their costs keep meaning what they meant on the day.
      </div>

      {recipes.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-panel p-8 text-center">
          <p className="text-[16px] font-semibold">No recipes yet.</p>
          <p className="mx-auto mt-2 max-w-[52ch] text-[14px] text-muted">
            A recipe needs a product and a decoration method, so these follow the
            catalogue import and the rate card. Until then a job consumes
            whatever is recorded against it by hand.
          </p>
        </div>
      ) : (
        [...groups.values()].map((versions) => {
          const head = versions[0]!;
          return (
            <section key={head.id} className="mb-6">
              <h2 className="mb-2 text-[15px] font-bold">
                {head.products?.name ?? 'Product'}{' '}
                <span className="text-muted">· {head.method_key}</span>
              </h2>

              {versions.map((r) => {
                const rows = byRecipe.get(r.id) ?? [];
                return (
                  <div
                    key={r.id}
                    className={`mb-3 rounded-[10px] border bg-panel ${
                      r.active ? 'border-line-strong' : 'border-line opacity-70'
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2">
                      <span className="font-semibold">Version {r.version}</span>
                      {r.active ? (
                        <Badge tone="ok">in use</Badge>
                      ) : (
                        <Badge tone="warn">superseded</Badge>
                      )}
                      {r.frozen && (
                        <span className="text-[13px] text-muted">
                          a job has used this, so it is read only
                        </span>
                      )}
                      <span className="ml-auto text-[13px] text-muted">
                        {new Date(r.created_at).toLocaleDateString('en-AU')}
                      </span>
                    </div>

                    {r.active && rows.length > 0 && (
                      <NewVersion recipeId={r.id} lines={rows} version={r.version} />
                    )}

                    {rows.length === 0 ? (
                      <p className="px-4 py-3 text-[14px] text-muted">
                        No lines. A recipe with no lines cannot be consumed: a job
                        cost of zero is never true.
                      </p>
                    ) : (
                      rows.map((l) => (
                        <div
                          key={l.id}
                          className="flex items-baseline justify-between gap-3 border-b
                                     border-line px-4 py-2 text-[14px] last:border-0"
                        >
                          <span className="truncate">
                            <span className="font-mono text-[13px] text-muted">
                              {l.items?.code}
                            </span>{' '}
                            {l.items?.name}
                          </span>
                          <span className="whitespace-nowrap">
                            {Number(l.qty_per_unit)} {l.uom}
                            {l.items && l.uom !== l.items.base_uom && (
                              <span className="ml-1 text-[13px] text-muted">
                                (held in {l.items.base_uom})
                              </span>
                            )}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </section>
          );
        })
      )}
    </div>
  );
}
